// bootApply — boot step 3 of 3, after the shell is mounted.
//
// See bootPrepare.js for the one-boot design. This step hands the resolved
// boot to the LIVE shell: the resolved interface language (setLang — loads
// nothing new, the bundles are in context.polyglot already), the
// theme-resolved watcher and the modal-host arm.
//
// Published shell only (`seed` given): the root state already existed when
// bootResolve ran, so its writes went into a seed object — they land on the
// live root here, BEFORE the shell's own auth pass reads
// `context.preResolvedAuth`; and the theme is corrected to the resolved mode
// when it differs from the stored one create() painted (the tenant default —
// the parcel boot passes the resolved mode to create() itself:
// `themeApplied: true`).
//
// Inputs: the shell element, bootResolve's result, { seed, themeApplied }.
import { DEFAULT_LANG } from './languages.js'
import { yieldToPaint } from './yieldToPaint.js'
import { watchThemeResolved } from './themeResolved.js'

export async function bootApply(shell, res, { seed = null, themeApplied = false } = {}) {
  if (!shell || !res || res.aborted) return shell
  // THE VERDICT REACHES THE LIVE SHELL (one-boot review D3). A mount receives
  // a COPY of the boot context — the warm-start early mount's copy is taken
  // before the gate even ran — so the gate's verdict is written onto the
  // context the mounted elements actually hold.
  if (shell.context && typeof shell.context === 'object') {
    if (typeof res.signedIn === 'boolean') shell.context.bootSignedIn = res.signedIn
    if (!shell.context.bootReady) shell.context.bootReady = Promise.resolve(res)
  }
  const _resolvedLang = res.lang
  const storage = res.storage

  if (seed && shell.state) {
    const _root = shell.state.root || shell.state
    const _keys = Object.keys(seed)
    if (_keys.length && typeof _root.update === 'function') {
      try {
        _root.update({ ...seed }, { preventFetch: true })
      } catch (err) {
        console.warn('[bootApply] could not seed the live root:', err?.message || err)
      }
    }
  }

  // The boot's workspace record may still owe the caller's `access` (bootResolve
  // reads it in the background): hand that read the LIVE root, so a landing after
  // the mount reaches it through the record owner. Not awaited; it never rejects.
  if (typeof res.completeWorkspaceAccess === 'function' && shell.state) {
    res.completeWorkspaceAccess(shell.state.root || shell.state)
  }

  if (!themeApplied && res.themeMode && res.themeMode !== res.storedThemeMode) {
    try {
      shell.call('changeGlobalTheme', res.themeMode)
    } catch (err) {
      console.warn('[bootApply] could not apply the resolved theme:', err?.message || err)
    }
  }

  // 8b. THE RESOLVED LANGUAGE MUST REACH THE **MOUNTED** APP.
  //
  // Step 7b writes `context.state.lang`. On the COLD path that write lands
  // before `create()`, so `prepareState` copies it into the root state and the
  // mount is already correct. On the WARM path the mount happened ~800 lines
  // ABOVE 7b, and the app DETACHED from the object 7b writes into: `create()`
  // spreads its options into a new object (smbls/packages/smbls/src/index.js),
  // `createDomql` then assigns `context.state = prepareState(app, context)`,
  // which `deepMerge`s into a FRESH object, and `element/create.js` takes a
  // further copy for the element's own state. Nothing overwrites `ka` with
  // `en`; the running app simply never held the object `ka` was written into.
  // That is why a returning member booted English while `<html lang>` — a
  // DOCUMENT write, and therefore global — read `ka` in the same boot.
  //
  // `setLang` is the one primitive that switches a LIVE element: it loads the
  // bundles, writes them into `context.polyglot`, calls the framework switch
  // (root state update + `[data-key]` re-render) and updates `<html lang>`.
  // `transient: true` is load-bearing: this language was RESOLVED, not chosen,
  // so it must persist nowhere — otherwise a workspace default stamps itself
  // into `localStorage.lang`, outranks `workspaceDefault` on the next boot, and
  // has impersonated a choice the member never made.
  //
  // Deliberately ONE path for both mounts rather than a branch inside the
  // warm-only block: the guard below is false on a correct cold boot (the root
  // already holds the resolved language), so this costs a comparison there, and
  // "after the mount, the live element holds the resolved language" is a single
  // statement instead of two that can drift apart. Guarded like the rest of the
  // language work — chrome must never stop a shell from mounting.
  //
  // ⚠️ THE `transient` GUARANTEE IS ENFORCED HERE, NOT ONLY ASKED FOR.
  // `el.call('setLang', …)` resolves through `context.functions`, and only a
  // consumer that merged @symbo-ls/workspace-shared has the platform's
  // three-argument `setLang` there. Everything else — a standalone, a context
  // assembled by hand — gets the FRAMEWORK's one-argument
  // `smbls/plugins/polyglot setLang(lang)`, which ignores the options bag and
  // writes `localStorage[storageLangKey]` unconditionally. That single write is
  // the impersonation bug in full: a workspace default lands in `lang`, comes
  // back on the next boot as `stored`, outranks `workspaceDefault` forever, and
  // no member can explain why their language changed. So bootShell snapshots
  // and restores the key around the call itself. Idempotent when the platform
  // `setLang` is the one that ran — it restores the same value first.
  if (shell && _resolvedLang) {
    try {
      const _liveState = shell.state?.root || shell.state
      // Deliver whenever the resolved language is not the base — NOT only when
      // the live state disagrees. On a warm mount the state already says `ka`
      // (it came from storage in step 1b) while the tree was rendered before
      // 7b merged a single Georgian bundle, so the "already correct" reading
      // was exactly the case that shipped an English page. `redeliver` pushes
      // the now-complete bundles through the mounted tree.
      if (_liveState && (_liveState.lang !== _resolvedLang || _resolvedLang !== DEFAULT_LANG)) {
        const _langBefore = storage?.getItem?.('lang') ?? null
        try {
          await shell.call('setLang', _resolvedLang, {
            transient: true,
            redeliver: _liveState.lang === _resolvedLang
          })
        } finally {
          try {
            if (_langBefore === null) storage?.removeItem?.('lang')
            else storage?.setItem?.('lang', _langBefore)
          } catch (_) {}
        }
      }
    } catch (err) {
      console.warn('[bootShell] could not apply the resolved language:', err?.message || err)
    }
  }

  // Wire the live `themeResolved` watcher onto the now-created root — one
  // singleton MutationObserver per document (see themeResolved.js), so
  // every future `data-theme` flip (a `changeGlobalTheme` call OR scratch's
  // own OS-driven 'auto' listener) keeps `root.themeResolved` correct for
  // the rest of the session with zero framework hook and zero duplicated
  // matchMedia logic. Runs for BOTH branches above (warm-start reconcile
  // and cold create()) — both are awaited ELEMENTS past this point, never a
  // pending Promise (see the cold-branch comment). Pinned by
  // bootShell.themeBootWiring.test.js.
  if (typeof document !== 'undefined' && shell?.state) {
    watchThemeResolved(shell.state.root || shell.state, document)
  }

  // WS-SHELL-MOUNT-TREE-BUILD-2700MS-1 — arm the deferred modal host.
  //
  // The shell mounts `ModalRoot` behind an `if: s.root._modalHostReady`
  // latch, so its 246 elements (measured 468 ms) stay OUT of the boot task.
  // `openModal` is a call, not a state carrier, and smbls flushes effects on
  // a microtask, so a first-open latch could not build the host in time for
  // the `resolveModalRoot` that the same call makes. Instead the latch flips
  // on ONE bounded yield right here: the shell is mounted, first paint has
  // happened, and nothing has been able to push a layer yet — every opener
  // stays untouched.
  //
  // Deliberately NOT awaited: the boot continues into its own continuation
  // while the host builds in the following frame. Apps that still mount
  // `ModalRoot` eagerly (canvas, preview) simply write a key nothing gates on.
  if (shell?.state) {
    const _shellRootState = shell.state.root || shell.state
    if (_shellRootState && !_shellRootState._modalHostReady) {
      const armModalHost = () =>
        _shellRootState.update({ _modalHostReady: true }, { preventFetch: true })
      // A HIDDEN document has no paint to yield to, and its timers are
      // throttled (1 s, then 1/min past ~5 minutes hidden) — measured live
      // 2026-08-22: a background-tab boot still had no modal host 18 s in,
      // which would make an `openModal` from a socket event in that tab warn
      // and no-op. That is a regression the eager mount never had, so a
      // hidden boot arms on a MessageChannel tick instead: a macrotask, so
      // the host still lands OUTSIDE the boot task, but an unthrottled one.
      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
      if (hidden && typeof MessageChannel === 'function') {
        const ch = new MessageChannel()
        ch.port1.onmessage = () => {
          ch.port1.close()
          armModalHost()
        }
        ch.port2.postMessage(0)
      } else {
        yieldToPaint().then(armModalHost)
      }
    }
  }

  // Seed Mongo-only user fields into the live root state. Schema
  // declarations on `context.state` only define the runtime DEFAULTS;
  // pre-create mutations on `context.state.user` / `currentUserUsername`
  // don't propagate to the reactive root in smbls 3.14 (the framework
  // re-initialises from the schema).
  // CORRECTED 2026-08-09 (found while wiring the warm-start reconcile
  // above, which relies on the SAME primitive): the earlier note here
  // claimed "create()'s return value doesn't expose an enumerable .update /
  // .state surface" — that is not what's actually going on. `shell` DOES
  // have both (`@symbo.ls/element`'s `addMethods` puts `.update` on every
  // element's prototype, including the root, and `.state` is set directly
  // in `createElementState`). What silently no-ops is calling the
  // ELEMENT-level `shell.update({ currentUserUsername, user })` directly:
  // `update.js`'s per-param loop only recurses into EXISTING CHILD elements
  // or creates NEW ones for uppercase keys — a lowercase, non-child key
  // like `currentUserUsername` falls through to "set it as a plain element
  // property," which is invisible to any `s.currentUserUsername` reactive
  // read. The primitive that DOES reach the reactive store — proven by the
  // warm-start reconcile a few lines up, and by the existing
  // `s.root.update(...)` calls in app.js's onCreate and throughout
  // auth.js/loadPrivateData.js — is `shell.state.update({...})` (or the
  // element-level `shell.update({ state: {...} })`, which routes to the
  // same place). Not fixed here — out of this ticket's scope, and
  // AppHeader.ProfileItem.onClick's lazy `el.sdk.getMe()` fetch already
  // covers the gap — but corrected so the note stops pointing the next
  // reader at a "framework limitation" that isn't one. Flagged per Nika's
  // "clean the file while you're in there" condition on this edit.

  // 9-10. Pattern A: single-app auto-dispatch + listener wiring.
  // Multi-app callers (workspace/index.js) handle steps 9-10 themselves
  // by calling dispatchUrl + wireGlobalListeners after bootShell returns.

  return shell
}
